import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, UserCog, Trash2, KeyRound, Check, X } from "lucide-react";

const ROLE_LABELS = {
  superadmin: { label: "Superadmin", tone: "bg-red-50 text-red-700 border-red-200" },
  manager:    { label: "Manager",    tone: "bg-blue-50 text-blue-700 border-blue-200" },
  executive:  { label: "Executive",  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  accountant: { label: "Accountant", tone: "bg-amber-50 text-amber-700 border-amber-200" },
};

const DEPT_LABELS = {
  admin: "Admin (HQ)",
  warehouse: "Warehouse",
  production_unit: "Production Unit",
  branch: "Branch",
  franchise: "Franchise",
};

const TABS = [
  { key: "account", label: "1. Account" },
  { key: "department", label: "2. Department" },
  { key: "access", label: "3. Access Limits" },
];

const emptyForm = () => ({
  name: "", mobile: "", password: "", employee_id: "",
  role: "executive",
  department: "branch", location_id: "", location_name: "",
  allowed_modules: [],
  is_active: true,
});

export default function Users() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [tab, setTab] = useState("account");
  const [catalog, setCatalog] = useState(null);
  const [locations, setLocations] = useState({ warehouse: [], production_unit: [], branch: [], franchise: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/users", { params: { search: search || undefined, include_inactive: true } });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [search]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/users/module-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    // Locations across all department types
    Promise.all([
      api.get("/production/warehouses").catch(() => ({ data: [] })),
      api.get("/production/units").catch(() => ({ data: [] })),
      api.get("/sales/branches").catch(() => ({ data: [] })),
    ]).then(([ws, us, bs]) => {
      const wh = (ws.data || []).filter((w) => w.is_active !== false);
      const un = (us.data || []).filter((u) => u.is_active !== false);
      const br = (bs.data || []).filter((b) => b.branch_type === "branch" && b.is_active !== false);
      const fr = (bs.data || []).filter((b) => b.branch_type === "franchise" && b.is_active !== false);
      setLocations({ warehouse: wh, production_unit: un, branch: br, franchise: fr });
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setTab("account");
    setModalOpen(true);
  };
  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      name: u.name || "", mobile: u.mobile || "", password: "", employee_id: u.employee_id || "",
      role: u.role || "executive",
      department: u.department || "branch", location_id: u.location_id || "", location_name: u.location_name || "",
      allowed_modules: u.allowed_modules || [],
      is_active: u.is_active !== false,
    });
    setTab("account");
    setModalOpen(true);
  };

  const onDepartmentChange = (dept) => {
    // Pre-fill module defaults for the newly picked department AND strip
    // keys the policy forbids for the new department.
    setForm((f) => {
      const posOnly = ["branch", "franchise"].includes(dept);
      const posKeys = catalog?.pos_module_keys || [];
      const cleaned = (f.allowed_modules || []).filter((m) => posOnly ? posKeys.includes(m) : !posKeys.includes(m));
      const withDefaults = cleaned.length ? cleaned : ((catalog?.defaults_by_department?.[dept]) || []);
      return {
        ...f,
        department: dept,
        location_id: dept === "admin" ? "" : f.location_id,
        location_name: dept === "admin" ? "" : f.location_name,
        allowed_modules: withDefaults,
      };
    });
  };

  const onLocationChange = (id) => {
    const list = locations[form.department] || [];
    const loc = list.find((l) => l.id === id);
    setForm((f) => ({ ...f, location_id: id, location_name: loc?.name || "" }));
  };

  const toggleModule = (key) => setForm((f) => ({
    ...f,
    allowed_modules: (f.allowed_modules || []).includes(key)
      ? f.allowed_modules.filter((m) => m !== key)
      : [...(f.allowed_modules || []), key],
  }));

  const bulkToggleSection = (keys, checked) => setForm((f) => {
    const set = new Set(f.allowed_modules || []);
    keys.forEach((k) => { if (checked) set.add(k); else set.delete(k); });
    return { ...f, allowed_modules: Array.from(set) };
  });

  const canSubmit = form.name.trim() && form.mobile.trim() && (editingId || form.password) &&
    (form.department === "admin" || form.location_id);

  const submit = async () => {
    if (!canSubmit) return toast.error("Complete Account + Department tabs before saving");
    setSaving(true);
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editingId}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setModalOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}? They will not be able to log in.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deactivated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalModules = catalog ? Object.values(catalog.sections).flat().length : 0;

  return (
    <div className="space-y-4">
      <PageSection
        title="Users & Privileges"
        description="Superadmin, Manager, Executive and Accountant accounts with per-department module access. Superadmin bypasses every restriction; other roles only see the modules ticked here."
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile, employee id…" className="pl-8" data-testid="users-search" />
            </div>
            <Button onClick={openNew} className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1" data-testid="users-new">
              <Plus className="h-4 w-4" /> New User
            </Button>
            <span className="text-xs text-gray-500 ml-auto">{rows.length} user(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2 w-32">Mobile</th>
                <th className="px-3 py-2 w-28">Role</th>
                <th className="px-3 py-2 w-36">Department</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2 text-center w-20">Modules</th>
                <th className="px-3 py-2 w-20 text-center">Status</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-14 text-center text-gray-400"><UserCog className="h-8 w-8 mx-auto mb-2 opacity-30" />No users yet — click <b>New User</b>.</td></tr>}
              {rows.map((u) => {
                const rl = ROLE_LABELS[u.role] || ROLE_LABELS.executive;
                const mods = u.role === "superadmin" ? totalModules : (u.allowed_modules || []).length;
                return (
                  <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{u.name}</div>
                      {u.employee_id && <div className="text-[11px] font-mono text-gray-500">{u.employee_id}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{u.mobile}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 ${rl.tone}`}>{rl.label}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">{DEPT_LABELS[u.department] || u.department}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{u.location_name || (u.department === "admin" ? "—" : <span className="text-red-500">(missing)</span>)}</td>
                    <td className="px-3 py-2 text-center text-xs font-semibold tabular-nums">{u.role === "superadmin" ? "ALL" : `${mods} / ${totalModules}`}</td>
                    <td className="px-3 py-2 text-center">
                      {u.is_active
                        ? <span className="text-[10px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">Active</span>
                        : <span className="text-[10px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 bg-gray-100 text-gray-500 border-gray-200">Inactive</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(u)} className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" data-testid={`user-edit-${u.id}`}>
                          <UserCog className="h-3.5 w-3.5 text-gray-500" />
                        </button>
                        {u.is_active && (
                          <button onClick={() => deactivate(u)} className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center" data-testid={`user-deactivate-${u.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Surface>
      </PageSection>

      {/* 3-tab modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit User" : "New User"}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-1 h-9 rounded-md border border-gray-200 p-0.5 bg-gray-50 mb-4">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`users-tab-${t.key}`}
                className={`flex-1 rounded text-xs font-semibold transition-colors ${tab === t.key ? "bg-white text-[#9B1C1C] shadow-sm" : "text-gray-500 hover:text-gray-900"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "account" && (
            <div className="grid grid-cols-2 gap-3">
              <F label="Full Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="users-name" /></F>
              <F label="Mobile *"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="9876543210" data-testid="users-mobile" /></F>
              <F label={editingId ? "New Password (blank to keep)" : "Password *"}>
                <div className="relative">
                  <KeyRound className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="min 6 chars" className="pl-8" data-testid="users-password" />
                </div>
              </F>
              <F label="Employee ID"><Input value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} data-testid="users-empid" /></F>
              <F label="Role *">
                <select value={form.role} onChange={(e) => set("role", e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="users-role">
                  {catalog?.roles?.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]?.label || r}</option>)}
                </select>
                {form.role === "superadmin" && <div className="text-[11px] text-red-600 mt-1">Superadmin bypasses every module check.</div>}
              </F>
              <F label="Active">
                <label className="inline-flex items-center gap-2 h-9">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="users-active" />
                  <span className="text-sm text-gray-700">Can log in</span>
                </label>
              </F>
            </div>
          )}

          {tab === "department" && (
            <div className="grid grid-cols-2 gap-3">
              <F label="Department *">
                <select value={form.department} onChange={(e) => onDepartmentChange(e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="users-dept">
                  {catalog?.departments?.map((d) => <option key={d} value={d}>{DEPT_LABELS[d] || d}</option>)}
                </select>
              </F>
              <F label={form.department === "admin" ? "Location" : "Location *"}>
                {form.department === "admin" ? (
                  <Input value="HQ (no location)" disabled className="bg-gray-50" />
                ) : (
                  <select value={form.location_id} onChange={(e) => onLocationChange(e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="users-loc">
                    <option value="">— Pick {DEPT_LABELS[form.department] || form.department} —</option>
                    {(locations[form.department] || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                )}
              </F>
              <p className="col-span-2 text-[11px] text-gray-500 mt-1">
                Pre-selecting a department fills the Access Limits tab with sensible defaults for that team — you can still override per module in step 3.
              </p>
            </div>
          )}

          {tab === "access" && (
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
              {form.role === "superadmin" ? (
                <div className="p-6 text-center text-sm bg-red-50 border border-red-200 rounded-md text-red-800">
                  <b>Superadmin</b> bypasses the module matrix — everything is accessible.
                </div>
              ) : catalog && Object.entries(catalog.sections).map(([section, mods]) => {
                const isPosSection = section === "POS Application";
                const isPosDept = (catalog.pos_only_departments || []).includes(form.department);
                // Show ONLY POS Application to branch/franchise; hide POS to others.
                if (isPosDept && !isPosSection) return null;
                if (!isPosDept && isPosSection) return null;
                const keys = mods.map((m) => m.key);
                const allOn = keys.every((k) => (form.allowed_modules || []).includes(k));
                const someOn = keys.some((k) => (form.allowed_modules || []).includes(k));
                return (
                  <div key={section} className="border border-gray-200 rounded-md">
                    <div className={`px-3 py-2 border-b border-gray-100 flex items-center gap-2 ${isPosSection ? "bg-red-50" : "bg-gray-50"}`}>
                      <input type="checkbox"
                        checked={allOn}
                        ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                        onChange={(e) => bulkToggleSection(keys, e.target.checked)}
                        data-testid={`users-section-${section.replace(/[^a-z]/gi, '').toLowerCase()}`}
                      />
                      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-700">{section}</span>
                      <span className="text-[11px] text-gray-500 ml-auto">{keys.filter((k) => (form.allowed_modules || []).includes(k)).length} / {keys.length}</span>
                    </div>
                    <div className="p-3 grid grid-cols-3 gap-2">
                      {mods.map((m) => (
                        <label key={m.key} className="inline-flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                          <input type="checkbox" checked={(form.allowed_modules || []).includes(m.key)} onChange={() => toggleModule(m.key)} data-testid={`users-mod-${m.key}`} />
                          <span>{m.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-gray-500 mt-2">
                Policy: <b>Branch / Franchise</b> staff only get <b>POS Application</b> modules. Other departments cannot access POS modules. Superadmin bypasses everything.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="users-cancel"><X className="h-4 w-4 mr-1" />Cancel</Button>
            <Button onClick={submit} disabled={!canSubmit || saving} className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" data-testid="users-save">
              <Check className="h-4 w-4 mr-1" />{saving ? "Saving…" : (editingId ? "Update User" : "Create User")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="flex flex-col text-xs">
      <span className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
